/**
 * PRP-IMPORT-PRICENOTE-1: price note helpers for the review page, kept out of
 * V2SupplementPage and ScopeTreeTable (god-file ratchet).
 */
import { useCallback, useEffect, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import type { ParsedEstimateResponse } from '../../types';
import type { ScopeTreeItem } from '../components/ScopeTreeTable';
import { payloadVersionOf } from './chatChanges';

/** One distinct unrecognized price text and the codes of the lines carrying it. */
export interface ParkedText {
  text: string;
  codes: string[];
}

/** Same comparison the backend list uses: case and spacing do not matter. */
const normalize = (text: string) => text.trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Text printed where a price goes that is not a recognized note, grouped by text.
 * A line the user has since marked with a note is no longer waiting on anything.
 */
export function parkedPriceTexts(res: ParsedEstimateResponse): ParkedText[] {
  const byText = new Map<string, ParkedText>();
  for (const room of res.estimate.rooms) {
    for (const li of room.line_items) {
      const text = li.parked_price_text?.trim();
      if (!text || li.price_note) continue;
      const key = normalize(text);
      const entry = byText.get(key) ?? { text, codes: [] };
      entry.codes.push(`${li.category} ${li.selector}`.trim());
      byText.set(key, entry);
    }
  }
  return [...byText.values()];
}

/**
 * The tree remounts when the estimate's version moves, which sends the reader back to the
 * top. Marking lines one after another should not, so a version a line edit produced does
 * not count, and {@link useTakePriceNotes} updates the rows in place.
 *
 * @returns The version for the tree's key, and the handler for a line edit's response.
 */
export function useTreeRevision(
  result: ParsedEstimateResponse,
  onResult: (_res: ParsedEstimateResponse) => void,
) {
  const version = payloadVersionOf(result);
  const fromLineEdit = useRef<string | undefined>(undefined);
  const treeRevision = useRef(version);
  if (version !== fromLineEdit.current) treeRevision.current = version;
  const onLineResult = useCallback(
    (res: ParsedEstimateResponse) => {
      fromLineEdit.current = payloadVersionOf(res);
      // The response carries the estimate; keep what only the page load sends (has_original).
      onResult({ ...result, ...res });
    },
    [result, onResult],
  );
  return { treeRevision: treeRevision.current, onLineResult };
}

/** Copies changed price notes from new props onto the table's local rows. */
export function useTakePriceNotes(
  incoming: ScopeTreeItem[],
  setItems: Dispatch<SetStateAction<ScopeTreeItem[]>>,
) {
  useEffect(() => {
    const byId = new Map(incoming.map((i) => [i.id, i]));
    setItems((prev) =>
      prev.map((x) => {
        const next = byId.get(x.id);
        return next && (next.priceNote ?? null) !== (x.priceNote ?? null)
          ? { ...x, priceNote: next.priceNote, unpriced: next.unpriced, total: next.total }
          : x;
      }),
    );
  }, [incoming, setItems]);
}
